import { useState } from 'react';
import { X, RefreshCw, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import type { Story, StoryPage, PageRegenerationResponse, AcceptImageResponse } from '@/types/story';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url: string | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

interface PageRegenerationModalProps {
  page: StoryPage;
  story: Story;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (pageNumber: number, newImageUrl: string) => void;
  onStart?: (pageNumber: number) => void;
  onEnd?: () => void;
}

type ModalState = 'preview' | 'generating' | 'compare' | 'accepting';

const PageRegenerationModal: React.FC<PageRegenerationModalProps> = ({
  page,
  story,
  isOpen,
  onClose,
  onSuccess,
  onStart,
  onEnd,
}) => {
  const [state, setState] = useState<ModalState>('preview');
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const [originalImageUrl] = useState<string>(page.image_url || '');
  const [customPrompt, setCustomPrompt] = useState<string>(page.image_prompt || '');
  const [usedCustomPrompt, setUsedCustomPrompt] = useState<string | null>(null);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [regenerationsRemaining, setRegenerationsRemaining] = useState<number | null>(null);

  const handleRegenerate = async () => {
    setState('generating');
    onStart?.(page.page_number);

    const promptToUse = showPromptEditor && customPrompt !== page.image_prompt
      ? customPrompt
      : undefined;

    try {
      const response = await axios.post<PageRegenerationResponse>(
        `${API}/stories/${story.id}/pages/${page.page_number}/regenerate-image`,
        promptToUse ? { custom_prompt: promptToUse } : {},
        { withCredentials: true }
      );

      setNewImageUrl(response.data.new_image_url);
      setUsedCustomPrompt(response.data.custom_prompt || null);
      setRegenerationsRemaining(response.data.regenerations_remaining);
      setState('compare');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to regenerate image';
      toast.error(message);
      setState('preview');
    } finally {
      onEnd?.();
    }
  };

  const handleAccept = async () => {
    if (!newImageUrl) return;

    setState('accepting');

    try {
      const response = await axios.post<AcceptImageResponse>(
        `${API}/stories/${story.id}/pages/${page.page_number}/accept-image`,
        {
          new_image_url: newImageUrl,
          custom_prompt: usedCustomPrompt,
        },
        { withCredentials: true }
      );

      setRegenerationsRemaining(response.data.regenerations_remaining);
      onSuccess(page.page_number, newImageUrl);
      toast.success(`Page ${page.page_number} image updated`);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to save image';
      toast.error(message);
      setState('compare');
    }
  };

  const handleCancel = () => {
    // Just close the comparison - image was never saved
    setState('preview');
    setNewImageUrl(null);
    setUsedCustomPrompt(null);
  };

  const handleClose = () => {
    if (state === 'generating' || state === 'accepting') return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: 'var(--st-bg-surface)',
          border: '1px solid var(--st-border-light)',
          boxShadow: '0 12px 32px rgba(92, 64, 51, 0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <h2
            className="text-lg font-bold"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            {state === 'compare' ? 'Compare Images' : `Regenerate Page ${page.page_number}`}
          </h2>
          <button
            onClick={handleClose}
            disabled={state === 'generating'}
            className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {state === 'preview' && (
            <div className="space-y-4">
              {/* Current image */}
              <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                {originalImageUrl ? (
                  <img
                    src={getImageUrl(originalImageUrl) || ''}
                    alt={`Page ${page.page_number}`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: 'var(--st-border-light)' }}
                  >
                    <span style={{ color: 'var(--st-text-muted)' }}>No image</span>
                  </div>
                )}
              </div>

              {/* Page text */}
              <div
                className="p-4 rounded-xl bg-orange-50/50 dark:bg-white/5"
                style={{ border: '1px solid var(--st-border-light)' }}
              >
                <p
                  className="text-sm"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
                >
                  {page.text}
                </p>
              </div>

              {/* Prompt editor toggle */}
              <button
                onClick={() => setShowPromptEditor(!showPromptEditor)}
                className="flex items-center gap-2 text-sm transition-colors hover:opacity-80 px-3 py-1.5 rounded-lg border"
                style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif', borderColor: 'var(--st-border-light)' }}
              >
                {showPromptEditor ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showPromptEditor ? 'Hide prompt editor' : 'Edit image prompt (optional)'}
              </button>

              {/* Prompt editor */}
              {showPromptEditor && (
                <div className="space-y-2">
                  <label
                    className="text-xs font-semibold"
                    style={{ color: 'var(--st-text-heading)', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    Image Prompt
                  </label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 dark:focus:ring-orange-700"
                    style={{
                      border: '1px solid var(--st-border-light)',
                      fontFamily: 'Quicksand, sans-serif',
                      color: 'var(--st-text-body)',
                      background: 'var(--st-bg-surface)',
                    }}
                    placeholder="Describe the image you want..."
                  />
                </div>
              )}
            </div>
          )}

          {(state === 'generating' || state === 'accepting') && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: 'var(--st-primary)' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                {state === 'accepting' ? 'Saving image...' : 'Generating new image...'}
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                {state === 'accepting' ? 'Almost done' : 'This may take a moment'}
              </p>
            </div>
          )}

          {state === 'compare' && newImageUrl && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Original */}
                <div className="space-y-2">
                  <span
                    className="text-sm font-semibold"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    Original
                  </span>
                  <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <img
                      src={getImageUrl(originalImageUrl) || ''}
                      alt="Original"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* New */}
                <div className="space-y-2">
                  <span
                    className="text-sm font-semibold"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}
                  >
                    New
                  </span>
                  <div
                    className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800"
                    style={{ border: '2px solid var(--st-primary)' }}
                  >
                    <img
                      src={getImageUrl(newImageUrl) || ''}
                      alt="New"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          {state === 'preview' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-orange-50 dark:hover:bg-orange-900/20"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  fontFamily: 'Quicksand, sans-serif',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif',
                  boxShadow: '0 6px 20px rgba(var(--st-glow-color), 0.4)',
                }}
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Regenerate Image
                </span>
              </button>
            </>
          )}

          {state === 'compare' && (
            <>
              {regenerationsRemaining !== null && (
                <span
                  className="text-xs mr-auto"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  {regenerationsRemaining} regeneration{regenerationsRemaining !== 1 ? 's' : ''} remaining
                </span>
              )}
              <button
                onClick={handleCancel}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-orange-50 dark:hover:bg-orange-900/20"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  fontFamily: 'Quicksand, sans-serif',
                }}
              >
                Keep Original
              </button>
              <button
                onClick={handleRegenerate}
                disabled={regenerationsRemaining === 0}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  fontFamily: 'Quicksand, sans-serif',
                }}
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </span>
              </button>
              <button
                onClick={handleAccept}
                className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif',
                  boxShadow: '0 6px 20px rgba(34, 197, 94, 0.4)',
                }}
              >
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Accept New Image
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageRegenerationModal;
